import React from 'react';
import { useAuth } from '../../contexts/AuthContext';
import DashboardHr from './DashboardHr';
import DashboardAccount from './DashboardAccount';
import DashboardTeacher from './DashboardTeacher';
import DashboardStudent from './DashboardStudent';
import {
    Users,
    GraduationCap,
    UserCheck,
    TrendingUp,
    Calendar,
    BookOpen,
    DollarSign,
    Code,
    TrendingDown,
    Activity,
    Award,
    Target
} from 'lucide-react';
import { Card } from '../../components/ui';

const Dashboard: React.FC = () => {
    const { user } = useAuth();

    // Route to appropriate dashboard based on user role
    if (user?.role === 'hr') {
        return <DashboardHr />;
    }

    if (user?.role === 'accountant') {
        return <DashboardAccount />;
    }

    if (user?.role === 'teacher') {
        return <DashboardTeacher />;
    }

    if (user?.role === 'student') {
        return <DashboardStudent />;
    }

    if (user?.role === 'principal') {
        return <DashboardHr />; // Principal gets HR dashboard
    }

    if (user?.role === 'clerk') {
        return <DashboardAccount />; // Clerk gets Account dashboard
    }

    // Default admin dashboard (existing code)
    const AdminDashboard: React.FC = () => {
        const stats = [
            {
                name: 'Total Students',
                value: '1,234',
                change: '+12%',
                changeType: 'positive',
                icon: GraduationCap,
                color: 'bg-gradient-to-r from-emerald-500 to-emerald-600',
                trend: 'up',
            },
            {
                name: 'Total Teachers',
                value: '89',
                change: '+5%',
                changeType: 'positive',
                icon: UserCheck,
                color: 'bg-gradient-to-r from-teal-500 to-teal-600',
                trend: 'up',
            },
            {
                name: 'Total Staff',
                value: '45',
                change: '+2%',
                changeType: 'positive',
                icon: Users,
                color: 'bg-gradient-to-r from-cyan-500 to-cyan-600',
                trend: 'up',
            },
            {
                name: 'Active Classes',
                value: '24',
                change: '+1',
                changeType: 'positive',
                icon: BookOpen,
                color: 'bg-gradient-to-r from-orange-500 to-orange-600',
                trend: 'up',
            },
        ];

        const financialMetrics = [
            {
                name: 'Monthly Revenue',
                value: '$45,230',
                change: '+12.5%',
                changeType: 'positive',
                icon: DollarSign,
                trend: 'up',
            },
            {
                name: 'Pending Fees',
                value: '$8,450',
                change: '-5.2%',
                changeType: 'negative',
                icon: TrendingDown,
                trend: 'down',
            },
            {
                name: 'Operating Costs',
                value: '$28,900',
                change: '+3.1%',
                changeType: 'positive',
                icon: Activity,
                trend: 'up',
            },
        ];

        const storeInventory = [
            { item: 'Textbooks', quantity: 1250, sold: 180, color: 'bg-emerald-500' },
            { item: 'Uniforms', quantity: 320, sold: 45, color: 'bg-teal-500' },
            { item: 'Stationery', quantity: 850, sold: 120, color: 'bg-cyan-500' },
            { item: 'Sports Equipment', quantity: 180, sold: 25, color: 'bg-orange-500' },
        ];

        const expenseBreakdown = [
            { category: 'Salaries', amount: 45000, percentage: 45, color: 'bg-emerald-500' },
            { category: 'Utilities', amount: 8000, percentage: 8, color: 'bg-teal-500' },
            { category: 'Maintenance', amount: 12000, percentage: 12, color: 'bg-cyan-500' },
            { category: 'Supplies', amount: 15000, percentage: 15, color: 'bg-orange-500' },
            { category: 'Transport', amount: 10000, percentage: 10, color: 'bg-purple-500' },
            { category: 'Others', amount: 10000, percentage: 10, color: 'bg-pink-500' },
        ];

        const chartData = {
            studentDistribution: [
                { grade: 'Grade 1-5', students: 420, color: 'bg-emerald-500' },
                { grade: 'Grade 6-8', students: 380, color: 'bg-teal-500' },
                { grade: 'Grade 9-10', students: 280, color: 'bg-cyan-500' },
                { grade: 'Grade 11-12', students: 154, color: 'bg-orange-500' },
            ],
            monthlyTrend: [
                { month: 'Jan', students: 1180, teachers: 85, revenue: 42000, expenses: 28000 },
                { month: 'Feb', students: 1195, teachers: 86, revenue: 43500, expenses: 28500 },
                { month: 'Mar', students: 1210, teachers: 87, revenue: 44200, expenses: 28900 },
                { month: 'Apr', students: 1225, teachers: 88, revenue: 44800, expenses: 29100 },
                { month: 'May', students: 1234, teachers: 89, revenue: 45230, expenses: 28900 },
            ],
            feeCollection: [
                { month: 'Jan', collected: 42000, pending: 8500, total: 50500 },
                { month: 'Feb', collected: 43500, pending: 8200, total: 51700 },
                { month: 'Mar', collected: 44200, pending: 8800, total: 53000 },
                { month: 'Apr', collected: 44800, pending: 8600, total: 53400 },
                { month: 'May', collected: 45230, pending: 8450, total: 53680 },
            ],
        };

        const recentActivities = [
            {
                id: 1,
                type: 'student',
                message: 'New student John Doe enrolled in Grade 10',
                time: '2 hours ago',
            },
            {
                id: 2,
                type: 'teacher',
                message: 'Teacher Sarah Wilson joined the Mathematics department',
                time: '4 hours ago',
            },
            {
                id: 3,
                type: 'staff',
                message: 'Staff member Mike Johnson updated his profile',
                time: '6 hours ago',
            },
            {
                id: 4,
                type: 'student',
                message: 'Student Emily Brown graduated from Grade 12',
                time: '1 day ago',
            },
        ];

        return (
            <div className="space-y-6">
                {/* Small Welcome Section */}
                <div className="flex items-center justify-between">
                    <div>
                        <h2 className="text-2xl font-bold gradient-text">Dashboard Overview</h2>
                        <p className="text-gray-600 text-sm">Welcome back! Here's what's happening at Noor-e-Hamza Islamic College</p>
                    </div>
                    <div className="flex items-center space-x-2 text-xs text-gray-500">
                        <Code className="h-3 w-3" />
                        <span>Developed by Dynamix Zone</span>
                    </div>
                </div>

                {/* Main Stats Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    {stats.map((stat) => (
                        <Card key={stat.name} glassy={true} className="islamic-pattern group hover:scale-105 transition-transform duration-300">
                            <div className="flex items-center justify-between">
                                <div className="flex-1">
                                    <p className="text-sm font-medium text-gray-600 mb-1">{stat.name}</p>
                                    <p className="text-3xl font-bold text-gray-900 mb-2">{stat.value}</p>
                                    <div className="flex items-center space-x-1">
                                        <TrendingUp className="h-4 w-4 text-green-500" />
                                        <p className="text-sm text-green-600 font-medium">{stat.change} from last month</p>
                                    </div>
                                </div>
                                <div className={`p-4 rounded-2xl ${stat.color} shadow-lg group-hover:shadow-xl transition-shadow duration-300`}>
                                    <stat.icon className="h-8 w-8 text-white" />
                                </div>
                            </div>
                        </Card>
                    ))}
                </div>

                {/* Financial Metrics */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {financialMetrics.map((metric) => (
                        <Card key={metric.name} glassy={true} className="islamic-pattern group hover:scale-105 transition-transform duration-300">
                            <div className="flex items-center justify-between">
                                <div className="flex-1">
                                    <p className="text-sm font-medium text-gray-600 mb-1">{metric.name}</p>
                                    <p className="text-2xl font-bold text-gray-900 mb-2">{metric.value}</p>
                                    <div className="flex items-center space-x-1">
                                        {metric.trend === 'up' ? (
                                            <TrendingUp className="h-4 w-4 text-green-500" />
                                        ) : (
                                            <TrendingDown className="h-4 w-4 text-red-500" />
                                        )}
                                        <p className={`text-sm font-medium ${metric.changeType === 'positive' ? 'text-green-600' : 'text-red-600'}`}>
                                            {metric.change} from last month
                                        </p>
                                    </div>
                                </div>
                                <div className="p-4 rounded-2xl bg-gradient-to-r from-emerald-500 to-teal-600 shadow-lg group-hover:shadow-xl transition-shadow duration-300">
                                    <metric.icon className="h-8 w-8 text-white" />
                                </div>
                            </div>
                        </Card>
                    ))}
                </div>

                {/* Charts and Analytics Section */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Student Distribution Pie Chart */}
                    <Card glassy={true} className="islamic-pattern">
                        <div className="flex items-center justify-between mb-6">
                            <h3 className="text-lg font-semibold text-gray-900">Student Distribution</h3>
                            <div className="p-2 rounded-lg bg-gradient-to-r from-emerald-100 to-teal-100">
                                <BookOpen className="h-5 w-5 text-emerald-600" />
                            </div>
                        </div>

                        {/* Pie Chart Visualization */}
                        <div className="flex items-center justify-center mb-6">
                            <div className="relative w-32 h-32">
                                {/* Pie Chart Sectors */}
                                <div className="absolute inset-0 rounded-full bg-gradient-to-r from-emerald-500 to-emerald-600 transform rotate-0"
                                    style={{ clipPath: 'polygon(50% 50%, 100% 0%, 100% 100%)' }}></div>
                                <div className="absolute inset-0 rounded-full bg-gradient-to-r from-teal-500 to-teal-600 transform rotate-90"
                                    style={{ clipPath: 'polygon(50% 50%, 100% 0%, 100% 100%)' }}></div>
                                <div className="absolute inset-0 rounded-full bg-gradient-to-r from-cyan-500 to-cyan-600 transform rotate-180"
                                    style={{ clipPath: 'polygon(50% 50%, 100% 0%, 100% 100%)' }}></div>
                                <div className="absolute inset-0 rounded-full bg-gradient-to-r from-orange-500 to-orange-600 transform rotate-270"
                                    style={{ clipPath: 'polygon(50% 50%, 100% 0%, 100% 100%)' }}></div>

                                {/* Center Circle */}
                                <div className="absolute inset-4 bg-white rounded-full flex items-center justify-center shadow-lg">
                                    <div className="text-center">
                                        <p className="text-lg font-bold text-gray-900">1,234</p>
                                        <p className="text-xs text-gray-500">Total</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Legend */}
                        <div className="space-y-3">
                            {chartData.studentDistribution.map((item, index) => (
                                <div key={index} className="flex items-center justify-between">
                                    <div className="flex items-center space-x-3">
                                        <div className={`w-3 h-3 rounded-full ${item.color}`}></div>
                                        <span className="text-sm font-medium text-gray-700">{item.grade}</span>
                                    </div>
                                    <span className="text-sm font-bold text-gray-900">{item.students}</span>
                                </div>
                            ))}
                        </div>
                    </Card>

                    {/* Revenue vs Expenses Chart */}
                    <Card glassy={true} className="islamic-pattern">
                        <div className="flex items-center justify-between mb-6">
                            <h3 className="text-lg font-semibold text-gray-900">Revenue vs Expenses</h3>
                            <div className="p-2 rounded-lg bg-gradient-to-r from-teal-100 to-cyan-100">
                                <DollarSign className="h-5 w-5 text-teal-600" />
                            </div>
                        </div>

                        {/* Financial Bar Chart */}
                        <div className="space-y-4">
                            {chartData.monthlyTrend.map((data, index) => (
                                <div key={index} className="space-y-2">
                                    <div className="flex items-center justify-between">
                                        <div className="text-xs text-gray-600 font-medium">{data.month}</div>
                                        <div className="text-xs text-gray-500">Profit: ${(data.revenue - data.expenses).toLocaleString()}</div>
                                    </div>
                                    <div className="flex items-center space-x-2">
                                        <div className="text-xs text-gray-600 w-16">Revenue</div>
                                        <div className="flex-1 bg-gray-200 rounded-full h-3">
                                            <div
                                                className="bg-gradient-to-r from-emerald-500 to-teal-500 h-3 rounded-full"
                                                style={{ width: `${(data.revenue / 50000) * 100}%` }}
                                            ></div>
                                        </div>
                                        <div className="text-xs font-medium text-gray-900 w-16">${data.revenue.toLocaleString()}</div>
                                    </div>
                                    <div className="flex items-center space-x-2">
                                        <div className="text-xs text-gray-600 w-16">Expenses</div>
                                        <div className="flex-1 bg-gray-200 rounded-full h-3">
                                            <div
                                                className="bg-gradient-to-r from-red-500 to-orange-500 h-3 rounded-full"
                                                style={{ width: `${(data.expenses / 30000) * 100}%` }}
                                            ></div>
                                        </div>
                                        <div className="text-xs font-medium text-gray-900 w-16">${data.expenses.toLocaleString()}</div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </Card>
                </div>

                {/* Store Inventory & Expense Breakdown */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Store Inventory */}
                    <Card glassy={true} className="islamic-pattern">
                        <div className="flex items-center justify-between mb-6">
                            <h3 className="text-lg font-semibold text-gray-900">Store Inventory</h3>
                            <div className="p-2 rounded-lg bg-gradient-to-r from-orange-100 to-red-100">
                                <BookOpen className="h-5 w-5 text-orange-600" />
                            </div>
                        </div>

                        {/* Inventory Bar Chart */}
                        <div className="space-y-4">
                            {storeInventory.map((item, index) => (
                                <div key={index} className="space-y-2">
                                    <div className="flex items-center justify-between">
                                        <span className="text-sm font-medium text-gray-700">{item.item}</span>
                                        <span className="text-xs text-gray-500">Sold: {item.sold}</span>
                                    </div>
                                    <div className="flex items-center space-x-2">
                                        <div className="text-xs text-gray-600 w-16">Stock</div>
                                        <div className="flex-1 bg-gray-200 rounded-full h-3">
                                            <div
                                                className={`${item.color} h-3 rounded-full`}
                                                style={{ width: `${(item.quantity / 1500) * 100}%` }}
                                            ></div>
                                        </div>
                                        <div className="text-xs font-medium text-gray-900 w-16">{item.quantity}</div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </Card>

                    {/* Expense Breakdown */}
                    <Card glassy={true} className="islamic-pattern">
                        <div className="flex items-center justify-between mb-6">
                            <h3 className="text-lg font-semibold text-gray-900">Expense Breakdown</h3>
                            <div className="p-2 rounded-lg bg-gradient-to-r from-purple-100 to-pink-100">
                                <Activity className="h-5 w-5 text-purple-600" />
                            </div>
                        </div>

                        {/* Expense Chart */}
                        <div className="space-y-4">
                            {expenseBreakdown.map((expense, index) => (
                                <div key={index} className="space-y-2">
                                    <div className="flex items-center justify-between">
                                        <span className="text-sm font-medium text-gray-700">{expense.category}</span>
                                        <span className="text-sm font-bold text-gray-900">${expense.amount.toLocaleString()}</span>
                                    </div>
                                    <div className="flex items-center space-x-2">
                                        <div className="text-xs text-gray-600 w-16">{expense.percentage}%</div>
                                        <div className="flex-1 bg-gray-200 rounded-full h-3">
                                            <div
                                                className={`${expense.color} h-3 rounded-full`}
                                                style={{ width: `${expense.percentage}%` }}
                                            ></div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </Card>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Recent Activities */}
                    <Card glassy={true} className="islamic-pattern">
                        <div className="flex items-center justify-between mb-6">
                            <h3 className="text-lg font-semibold text-gray-900">Recent Activities</h3>
                            <div className="p-2 rounded-lg bg-gradient-to-r from-orange-100 to-red-100">
                                <Calendar className="h-5 w-5 text-orange-600" />
                            </div>
                        </div>
                        <div className="space-y-4">
                            {recentActivities.map((activity) => (
                                <div key={activity.id} className="flex items-start space-x-3 p-3 rounded-xl hover:bg-white/20 transition-colors duration-200">
                                    <div className="flex-shrink-0">
                                        <div className={`w-3 h-3 rounded-full mt-2 ${activity.type === 'student' ? 'bg-emerald-500' :
                                            activity.type === 'teacher' ? 'bg-teal-500' : 'bg-cyan-500'
                                            }`} />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm text-gray-900 font-medium">{activity.message}</p>
                                        <p className="text-xs text-gray-500 mt-1">{activity.time}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </Card>

                    {/* Quick Actions */}
                    <Card glassy={true} className="islamic-pattern">
                        <div className="flex items-center justify-between mb-6">
                            <h3 className="text-lg font-semibold text-gray-900">Quick Actions</h3>
                            <div className="p-2 rounded-lg bg-gradient-to-r from-purple-100 to-pink-100">
                                <TrendingUp className="h-5 w-5 text-purple-600" />
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <a
                                href="/students/new"
                                className="p-4 border border-white/20 rounded-xl hover:bg-white/10 transition-all duration-200 backdrop-blur-sm group"
                            >
                                <GraduationCap className="h-6 w-6 text-emerald-500 mb-2 group-hover:scale-110 transition-transform" />
                                <p className="text-sm font-medium text-gray-900">Add Student</p>
                            </a>
                            <a
                                href="/teachers/new"
                                className="p-4 border border-white/20 rounded-xl hover:bg-white/10 transition-all duration-200 backdrop-blur-sm group"
                            >
                                <UserCheck className="h-6 w-6 text-teal-500 mb-2 group-hover:scale-110 transition-transform" />
                                <p className="text-sm font-medium text-gray-900">Add Teacher</p>
                            </a>
                            <a
                                href="/staff/new"
                                className="p-4 border border-white/20 rounded-xl hover:bg-white/10 transition-all duration-200 backdrop-blur-sm group"
                            >
                                <Users className="h-6 w-6 text-cyan-500 mb-2 group-hover:scale-110 transition-transform" />
                                <p className="text-sm font-medium text-gray-900">Add Staff</p>
                            </a>
                            <a
                                href="/reports"
                                className="p-4 border border-white/20 rounded-xl hover:bg-white/10 transition-all duration-200 backdrop-blur-sm group"
                            >
                                <DollarSign className="h-6 w-6 text-orange-500 mb-2 group-hover:scale-110 transition-transform" />
                                <p className="text-sm font-medium text-gray-900">View Reports</p>
                            </a>
                        </div>
                    </Card>
                </div>
            </div>
        );
    };

    return <AdminDashboard />;
};

export default Dashboard;
